import Button from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardCloseButton,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import Input from "@/components/ui/input";
import { useUpdateThemeMutation } from "@/shared/services";
import type { TDesignSystem } from "@/shared/types";
import Spinner from "@components/ui/spinner";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { useState, type KeyboardEventHandler } from "react";
import { toast } from "react-toastify";

const DIALOG_ID = "rename-theme-dialog";

interface IProps {
  theme: TDesignSystem;
}

export const RenameThemeDialog = ({ theme }: IProps) => {
  const dispatch = useAppDispatch();
  const [title, setTitle] = useState(theme.title);
  const { mutateAsync: updateTheme, isPending: isLoading } =
    useUpdateThemeMutation();

  const handleClose = () => {
    dispatch(closeDialog(DIALOG_ID));
  };

  const handleSubmit = async () => {
    if (isLoading || !title.trim()) return;

    try {
      await updateTheme({
        title: title.trim(),
        payload: theme.designSystem,
        id: theme._id,
      });
      toast.success("Theme renamed successfully!");
      handleClose();
    } catch {
      // Keep the dialog open so the user can fix the name and retry.
      toast.error("An error occurred, please try again.");
    }
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  return (
    <Card>
      <CardHeader className="tw-gap-0">
        <CardTitle>Rename theme</CardTitle>
        <CardAction>
          <CardCloseButton onClick={handleClose} />
        </CardAction>
      </CardHeader>
      <CardContent>
        <Input
          value={title}
          className={cn(
            "tw-w-full",
            !title.trim() && "tw-border-[#D43548] tw-bg-[#FAF1F2]",
          )}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        {!title.trim() && (
          <span className="tw-mt-2 tw-block tw-font-sans tw-text-[11px]/tight tw-font-normal tw-text-[#D43548]">
            Name field can't be empty.
          </span>
        )}
      </CardContent>
      <CardFooter className="tw-justify-end tw-gap-3">
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!title.trim()}
          className={cn(isLoading && "tw-cursor-not-allowed")}
        >
          {isLoading ? (
            <>
              <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
              Saving...
            </>
          ) : (
            "Save changes"
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};

RenameThemeDialog.dialogId = DIALOG_ID;
